import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import * as api from "../api/client";
import { ApiError } from "../api/client";
import { getToken, setToken, clearToken } from "../api/authToken";

type AuthStatus = "checking" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  error: string | null;
  login: (secret: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No stored token at all means "definitely not logged in" - skip the
    // network round trip entirely rather than making a call we already
    // know will 401.
    if (!getToken()) {
      setStatus("unauthenticated");
      return;
    }

    let cancelled = false;
    api
      .getSession()
      .then(() => {
        if (!cancelled) setStatus("authenticated");
      })
      .catch(() => {
        if (!cancelled) {
          clearToken();
          setStatus("unauthenticated");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (secret: string) => {
    setError(null);
    try {
      const result = await api.login(secret);
      setToken(result.token);
      setStatus("authenticated");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code === "RATE_LIMITED" ? err.message : "Invalid administrator secret.");
      } else {
        setError("Could not reach the API. Check the API URL and try again.");
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      clearToken();
      setStatus("unauthenticated");
    }
  }, []);

  return (
    <AuthContext.Provider value={{ status, error, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
